export const OFFLINE_ADHKAR_CHANGED = 'amposter:offline-adhkar-changed';
export const OFFLINE_ADHKAR_SYNCED = 'amposter:offline-adhkar-synced';

let database;
const pending = new Map();
const open = () => {
  if (!database) database = new Promise((resolve, reject) => {
    const request = indexedDB.open('amposter-offline-adhkar-progress', 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore('keys', { keyPath: 'accountId' });
      request.result.createObjectStore('days', { keyPath: 'accountId' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }).catch(error => { database = null; throw error; });
  return database;
};

async function row(store, accountId) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const request = db.transaction(store).objectStore(store).get(accountId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function put(store, value) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value);
    tx.oncomplete = resolve;
    tx.onerror = tx.onabort = () => reject(tx.error);
  });
}

async function keyFor(accountId) {
  const saved = await row('keys', accountId);
  if (saved) return saved.key;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await put('keys', { accountId, key });
  return key;
}

async function read(accountId) {
  const saved = await row('days', accountId);
  if (!saved) return { progress: {}, days: [] };
  const key = await keyFor(accountId);
  const bytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: saved.iv }, key, saved.ciphertext);
  const ledger = JSON.parse(new TextDecoder().decode(bytes));
  if (!ledger || typeof ledger !== 'object' || !Array.isArray(ledger.days)) throw new Error('Invalid offline adhkar ledger');
  return ledger;
}

async function write(accountId, ledger) {
  const key = await keyFor(accountId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify(ledger)));
  await put('days', { accountId, iv, ciphertext });
}

function serialized(accountId, action) {
  const previous = pending.get(accountId) || Promise.resolve();
  const next = previous.catch(() => {}).then(action);
  pending.set(accountId, next);
  next.finally(() => { if (pending.get(accountId) === next) pending.delete(accountId); }).catch(() => {});
  return next;
}

export function recordOfflineAdhkarTap(accountId, date, period, itemId, items) {
  if (!accountId || accountId === 'guest' || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !['morning', 'evening'].includes(period) || !items.length) return Promise.resolve();
  const chosen = items.find(item => String(item.id) === String(itemId));
  if (!chosen) return Promise.resolve();
  const name = period.toUpperCase(), progressKey = `${date}:${name}`;
  return serialized(accountId, async () => {
    const ledger = await read(accountId);
    ledger.progress[progressKey] ||= {};
    const counts = ledger.progress[progressKey];
    counts[String(itemId)] = Math.min(chosen.repeat, (counts[String(itemId)] || 0) + 1);
    const complete = items.every(item => (counts[String(item.id)] || 0) >= item.repeat) &&
        !ledger.days.some(day => day.date === date && day.period === name);
    if (complete) {
      ledger.days.push({ date, period: name, items: items.map(item => ({ itemKey: String(item.id), count: item.repeat, targetCount: item.repeat })) });
    }
    await write(accountId, ledger);
    if (complete) window.dispatchEvent(new Event(OFFLINE_ADHKAR_CHANGED));
  });
}

export const pendingOfflineAdhkar = accountId => serialized(accountId, async () =>
  (await read(accountId)).days.sort((a, b) => a.date.localeCompare(b.date)).slice(0, 90));

export const acknowledgeOfflineAdhkar = (accountId, snapshot) => serialized(accountId, async () => {
  const acknowledged = new Set(snapshot.map(day => `${day.date}:${day.period}`));
  const ledger = await read(accountId);
  ledger.days = ledger.days.filter(day => !acknowledged.has(`${day.date}:${day.period}`));
  acknowledged.forEach(key => { delete ledger.progress[key]; });
  await write(accountId, ledger);
});
